import {
  Close,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollEdges } from '@/shared/hooks/useScrollEdges/useScrollEdges';
import { cn } from '@/shared/utils/cn/cn.util';

import { dialogStackClasses, useDialogStack } from '../DialogStack/DialogStack';
import { SCROLL_EDGE_CLASSES, SCROLLER_CLASSES } from '../scroll/scroll.tokens';

import {
  DIALOG_CONTAINMENT_CLASSES,
  DIALOG_POSITION_CLASSES,
  dialogSizeVariants,
  OVERLAY_CLASSES,
  OVERLAY_CLOSE_CLASSES,
  OVERLAY_CLOSE_ROW_CLASSES,
  type DialogSizeVariants,
} from './Dialog.tokens';

export const Dialog = Root;
export const DialogTrigger = Trigger;
export const DialogClose = Close;
export const DialogPortal = Portal;

export type DialogOverlayProps = ComponentPropsWithoutRef<typeof Overlay>;

export const DialogOverlay = forwardRef<ElementRef<typeof Overlay>, DialogOverlayProps>(
  function DialogOverlay({ className, ...props }, ref) {
    return <Overlay ref={ref} className={cn(OVERLAY_CLASSES, className)} {...props} />;
  },
);

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof Content>, DialogSizeVariants {
  /** Hides the built-in close button, for a dialog whose dismissal is deliberate. */
  hideClose?: boolean;
}

export const DialogContent = forwardRef<ElementRef<typeof Content>, DialogContentProps>(
  function DialogContent({ className, children, size, hideClose = false, ...props }, ref) {
    const { t } = useTranslation('uiKit');
    // Position in the modal stack, published by `GlobalModalManager` from the
    // slice's array index. Outside the manager this is `{ depth: 0, total: 1 }`,
    // so a page-local dialog behaves exactly as it did before the stack existed.
    const stack = useDialogStack();

    return (
      <DialogPortal>
        <DialogOverlay />
        <Content
          ref={ref}
          className={cn(
            // A centred card from `sm` up, a bottom sheet below it; the clamp
            // (`max-h-dialog` / `max-h-sheet-mobile`) and the flex COLUMN that
            // makes it mean something both live in the position tokens, because
            // the two differ per breakpoint and reading them apart is how they
            // drift.
            DIALOG_POSITION_CLASSES,
            // Whether the content box clips or scrolls, decided by whether the
            // caller composed a `DialogBody`. Neither branch can clip.
            DIALOG_CONTAINMENT_CLASSES,
            dialogStackClasses(stack),
            dialogSizeVariants({ size }),
            className,
          )}
          {...props}
        >
          {/* The grabber of a bottom sheet, below `sm` only. Decoration, not a
              control — the sheet is dismissed by the close button, Escape, or
              the backdrop — but it is the shape a phone user reads as "this
              panel came up from the bottom", and its absence is what makes a
              full-bleed dialog look like a broken page. */}
          <div
            aria-hidden
            className="mx-auto -mt-sm mb-xs h-xs w-2xl shrink-0 rounded-full bg-border sm:hidden"
          />
          {children}
          {hideClose ? null : (
            // Sticky, not absolute — see `OVERLAY_CLOSE_ROW_CLASSES` for the
            // bodyless dialog that scrolled its own ✕ off the top.
            <div className={OVERLAY_CLOSE_ROW_CLASSES}>
              <Close
                // The label comes from the kit catalogue, so it is Arabic by
                // default like everything else — an icon-only button with a
                // hardcoded English label is the classic kit i18n hole.
                aria-label={t('modal.close')}
                className={OVERLAY_CLOSE_CLASSES}
              >
                <X aria-hidden className="h-4 w-4" />
              </Close>
            </div>
          )}
        </Content>
      </DialogPortal>
    );
  },
);

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // `shrink-0` + `pe-2xl`: the header never gives up height to a long body, and it
  // keeps clear of the sticky close row that floats above it.
  return (
    <div className={cn('flex shrink-0 flex-col gap-xs pe-2xl text-start', className)} {...props} />
  );
}

/**
 * The one scrolling region of a dialog.
 *
 * The behaviour comes from `SCROLLER_CLASSES` (see `scroll.tokens.ts` for what
 * each class is holding up — `min-h-0` above all, without which the clamp on the
 * content box silently stops meaning anything).
 *
 * Anything long belongs in here — a permission checklist, a field set, a document
 * preview. What must NOT is the footer: an action row inside the scroller is an
 * action row the operator has to go looking for, which is exactly how P9's role
 * form ended up with an unreachable Save button.
 *
 * Two things it publishes:
 *
 *   `data-dialog-body`   what `DIALOG_CONTAINMENT_CLASSES` looks for. Its
 *                        presence is why the content box may clip: something
 *                        inside it scrolls.
 *   `data-scroll-start`  / `data-scroll-end` — the edge state, so a separator
 *                        appears under the header only while there is content
 *                        above, and over the footer only while there is content
 *                        below. A permanent rule communicates nothing; its
 *                        absence made a scrolled body look exactly like a
 *                        clipped one, which is the confusion this phase exists
 *                        to end.
 */
export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const { ref, isAtStart, isAtEnd } = useScrollEdges();

  return (
    <div
      ref={ref}
      data-dialog-body=""
      data-scroll-start={isAtStart}
      data-scroll-end={isAtEnd}
      className={cn(
        'flex flex-col gap-lg',
        // Full-bleed: the body's box spans the content padding so its edge rules
        // reach the dialog's borders instead of floating 24px short of them, and
        // the scrollbar rides the dialog's edge where the thumb is expected.
        // The padding is given back on the inside, so the content does not move.
        '-mx-xl px-xl',
        SCROLLER_CLASSES,
        SCROLL_EDGE_CLASSES,
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      // Column on a narrow viewport (a stacked Cancel/Save is easier to hit),
      // row-reverse above it so the primary action sits at the inline end.
      //
      // `shrink-0` pins it against a scrolling `DialogBody`.
      //
      // No border of its own any more: the separator is the BODY's, drawn only
      // while there is still content below (`data-scroll-end=false`). A rule that
      // is always there says nothing, and here it said the wrong thing — it made
      // a body with three fields look as cut off as one with thirty.
      className={cn(
        'flex shrink-0 flex-col-reverse gap-sm pt-sm sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export type DialogTitleProps = ComponentPropsWithoutRef<typeof Title>;
export type DialogDescriptionProps = ComponentPropsWithoutRef<typeof Description>;

export const DialogTitle = forwardRef<ElementRef<typeof Title>, DialogTitleProps>(
  function DialogTitle({ className, ...props }, ref) {
    return <Title ref={ref} className={cn('text-h3 text-foreground', className)} {...props} />;
  },
);

export const DialogDescription = forwardRef<ElementRef<typeof Description>, DialogDescriptionProps>(
  function DialogDescription({ className, ...props }, ref) {
    return (
      <Description
        ref={ref}
        className={cn('text-caption text-muted-foreground', className)}
        {...props}
      />
    );
  },
);
